import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import Navigation from './components/Navigation';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import McpPage from './pages/McpPage';
import ProfileAdminPage from './pages/ProfileAdminPage';
import ProfileAdminSignupInvitePage from './pages/ProfileAdminSignupInvitePage';
import { ChartPage } from './pages/ChartPage';
import { CryptoPage } from './pages/CryptoPage';
import AIAgentPage from './pages/AIAgentPage';
import OpportunitySignalsPage from './pages/OpportunitySignalsPage';
import GlobalChatPage from './pages/GlobalChatPage';
import QuantAgiPage from './pages/QuantAgiPage';
import DeployStrategiesPage from './pages/DeployStrategiesPage';
import { AuthenticatedChatLayer } from './components/AuthenticatedChatLayer';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { COMMUNITY_CHAT_ENABLED, QUANT_AGI_ENABLED } from './config/features';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return isAuthenticated ? <Navigate to="/dashboard" /> : <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <AuthenticatedChatLayer>
      <div className="relative min-h-screen text-kib-fg app-shell">
        <Navigation />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } />
            <Route path="/reset-password" element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <SocketProvider>
                  <AIAgentPage />
                </SocketProvider>
              </ProtectedRoute>
            } />
            <Route path="/ai-agent" element={<Navigate to="/dashboard" replace />} />
            <Route path="/mcp" element={
              <ProtectedRoute>
                <McpPage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/profile/admin" element={
              <ProtectedRoute>
                <ProfileAdminPage />
              </ProtectedRoute>
            } />
            <Route path="/profile/signup-invite-admin" element={
              <ProtectedRoute>
                <ProfileAdminSignupInvitePage />
              </ProtectedRoute>
            } />
            <Route path="/charts" element={
              <ProtectedRoute>
                <SocketProvider>
                  <ChartPage />
                </SocketProvider>
              </ProtectedRoute>
            } />
            <Route path="/crypto" element={
              <ProtectedRoute>
                <SocketProvider>
                  <CryptoPage />
                </SocketProvider>
              </ProtectedRoute>
            } />
            <Route path="/opportunity-signals" element={
              <ProtectedRoute>
                <SocketProvider>
                  <OpportunitySignalsPage />
                </SocketProvider>
              </ProtectedRoute>
            } />
            <Route
              path="/chat"
              element={
                COMMUNITY_CHAT_ENABLED ? (
                  <ProtectedRoute>
                    <GlobalChatPage />
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/deploy-strategies"
              element={
                <ProtectedRoute>
                  <DeployStrategiesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quant-agi"
              element={
                QUANT_AGI_ENABLED ? (
                  <ProtectedRoute>
                    <QuantAgiPage />
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            {/* Quant AGI subroutes: redirect while the feature is off. */}
            <Route
              path="/quant-agi/*"
              element={<Navigate to={QUANT_AGI_ENABLED ? '/quant-agi' : '/dashboard'} replace />}
            />
          </Routes>
        </main>
        <Toaster 
          position="top-center"
          containerStyle={{ top: 'max(12px, env(safe-area-inset-top))' }}
          toastOptions={{
            duration: 4000,
            style: {
              background: '#161b22',
              color: '#e6edf3',
              borderRadius: '8px',
              border: '1px solid rgba(240, 246, 252, 0.1)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
              maxWidth: 'min(100vw - 24px, 420px)',
            },
            success: {
              iconTheme: {
                primary: '#00c805',
                secondary: '#0c1526',
              },
            },
            error: {
              iconTheme: {
                primary: '#ff5000',
                secondary: '#0c1526',
              },
            },
          }}
        />
      </div>
      </AuthenticatedChatLayer>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
};

export default App;
